import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TabRecorder, playWithComments, loadYouTubeApi } from "@/lib/recorder";
import { getVoiceClip } from "@/lib/voiceClips";
import { loadTagPresets, loadDefaultPrivacy } from "@/lib/studioUtils";
import { VoiceStage, DEFAULT_BARS_POS } from "./studio/VoiceStage";
import { UnderVideoBar } from "./studio/UnderVideoBar";
import { CommentDialog } from "./studio/CommentDialog";
import { CommentsPanel } from "./studio/CommentsPanel";
import { CommentTimeline } from "./studio/CommentTimeline";
import { useComments } from "./studio/useComments";
import { useCommentPreview } from "./studio/useCommentPreview";
import { useEditorPlayback } from "./studio/useEditorPlayback";
import { aiError } from "./studio/aiError";
import { PublishDetails } from "./studio/PublishDetails";
import { PublishVideoDialog } from "./studio/PublishVideoDialog";
import { VoiceDialog } from "./studio/VoiceDialog";
import { PublishResultDialog } from "./studio/PublishResultDialog";

const settingsAction = { label: "Open Settings", onClick: () => { window.location.href = "/settings"; } };

export default function Studio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState(null); // null | "recording" | progress text
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);
  const [voice, setVoice] = useState("onyx");
  const [barsPos, setBarsPos] = useState(DEFAULT_BARS_POS);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentTime, setCommentTime] = useState(0);
  const [editing, setEditing] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [pubTitle, setPubTitle] = useState("");
  const [pubDesc, setPubDesc] = useState("");
  const [privacy, setPrivacy] = useState(loadDefaultPrivacy());
  const [defaultTags, setDefaultTags] = useState(loadTagPresets());
  const [defaultTagInput, setDefaultTagInput] = useState("");
  const { comments, improvingIds, add: addComment, repolish: repolishComment, change: changeComment, retime: retimeComment, remove: removeComment } = useComments(project);
  const { preview, togglePreview, stopPreview } = useCommentPreview(voice);

  const playerHostRef = useRef(null);
  const playerRef = useRef(null);
  const stageRef = useRef(null);
  const recorderRef = useRef(null);
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const lastBlobRef = useRef(null);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => {
      const p = r.data;
      if (p.voice) setVoice(p.voice);
      if (p.bars_pos) setBarsPos(p.bars_pos);
      setPubTitle(p.pub_title ?? (p.source_title ? `My reaction to ${p.source_title}` : "My Reaction Video").slice(0, 100));
      setPubDesc(p.pub_desc ?? baseDescription(p));
      setProject(p);
    }).catch(() => navigate("/dashboard"));
  }, [projectId, navigate]);

  // Keep edited title + description with the project (debounced).
  const detailsTimer = useRef(null);
  useEffect(() => {
    if (!project) return;
    clearTimeout(detailsTimer.current);
    detailsTimer.current = setTimeout(() => {
      api.patch(`/projects/${projectId}`, { pub_title: pubTitle, pub_desc: pubDesc }).catch(() => {});
    }, 800);
    return () => clearTimeout(detailsTimer.current);
  }, [pubTitle, pubDesc]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveChoice = (patch) => api.patch(`/projects/${projectId}`, patch).catch(() => {});
  const pickVoice = (id) => { setVoice(id); saveChoice({ voice: id }); };
  const moveBars = (pos) => { setBarsPos(pos); saveChoice({ bars_pos: pos }); };

  // YouTube source player
  useEffect(() => {
    if (!project) return;
    let destroyed = false;
    loadYouTubeApi().then((YT) => {
      if (destroyed || !playerHostRef.current) return;
      playerRef.current = new YT.Player(playerHostRef.current, {
        videoId: project.video_id,
        host: "https://www.youtube-nocookie.com",
        width: "100%", height: "100%",
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
      });
    });
    return () => { destroyed = true; try { playerRef.current?.destroy(); } catch (e) { /* noop */ } };
  }, [project]);

  const pausePlayer = () => { try { playerRef.current?.pauseVideo(); } catch (e) { /* noop */ } };
  const playerTime = () => { try { return playerRef.current?.getCurrentTime?.() || 0; } catch (e) { return 0; } };
  const seekTo = (t) => { try { playerRef.current?.seekTo(t, true); pausePlayer(); } catch (e) { /* noop */ } };
  const openCommentDialog = () => { pausePlayer(); stopPreview(); setEditing(null); setCommentTime(playerTime()); setCommentOpen(true); };
  const openEditDialog = (c) => { stopPreview(); seekTo(c.t); setEditing(c); setCommentTime(c.t); setCommentOpen(true); };

  useEffect(() => () => {
    clearInterval(timerRef.current);
    abortRef.current?.abort();
    recorderRef.current?.abort();
  }, []);

  const prepareClips = async () => {
    const urls = [];
    for (let i = 0; i < comments.length; i++) {
      setStatus(`Preparing voice ${i + 1} of ${comments.length}…`);
      urls.push(await getVoiceClip(comments[i].text, voice));
    }
    return urls;
  };

  const assembleDescription = () => {
    const tags = defaultTags.map((t) => t.replace(/^#/, "").trim()).filter(Boolean).map((t) => `#${t}`);
    return [pubDesc.trim(), ...(tags.length ? ["", tags.join(" ")] : [])].join("\n");
  };

  const uploadBlob = async (blob) => {
    setStatus("Uploading to YouTube…");
    try {
      const form = new FormData();
      form.append("file", blob, "reaction.webm");
      form.append("title", pubTitle.trim());
      form.append("description", assembleDescription());
      form.append("privacy", privacy);
      const r = await api.post(`/projects/${projectId}/publish-direct`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      lastBlobRef.current = null;
      setPublishResult({ ok: true, url: r.data.youtube_url });
    } catch (e) {
      setPublishResult({ ok: false, canRetry: true, message: e.response?.data?.detail });
    } finally {
      setStatus(null);
    }
  };

  // Publish = record the play-through (source + AI voice + bars) and upload it in one go.
  const publishVideo = async () => {
    if (!comments.length) { toast.error("Add at least one comment first"); return; }
    if (improvingIds.size) { toast.error("Wait for AI to finish polishing your comments"); return; }
    if (comments.some((c) => !c.text.trim())) { toast.error("One of your comments is empty"); return; }
    const yt = await api.get("/youtube/status").catch(() => ({ data: {} }));
    if (!yt.data.connected) { toast.error("Connect your YouTube channel in Settings first", { action: settingsAction }); return; }
    setPublishOpen(false);
    stopPreview();
    try { playerRef.current?.seekTo(0, true); } catch (e) { /* noop */ }
    pausePlayer();
    let urls;
    try { urls = await prepareClips(); } catch (e) { setStatus(null); await aiError(e, "Couldn't create the voice. Please try again."); return; }
    setStatus("Choose This Tab and turn on 'Also share tab audio'…");
    const rec = new TabRecorder({ sourceEl: stageRef.current });
    rec.onTabEnded = () => finishRecording();
    let hasTabAudio;
    try { hasTabAudio = await rec.prepare(); } catch {
      setStatus(null);
      toast.error("Tab sharing was cancelled. Pick 'This Tab' and turn on 'Also share tab audio'.");
      return;
    }
    if (!hasTabAudio) {
      rec.abort();
      setStatus(null);
      toast.error("Tab audio is needed for the voice. Publish again and turn on 'Also share tab audio'.");
      return;
    }
    recorderRef.current = rec;
    cancelledRef.current = false;
    setStatus(null);
    for (let n = 3; n >= 1; n--) { setCountdown(n); await new Promise((r) => setTimeout(r, 1000)); }
    setCountdown(null);
    rec.begin();
    setStatus("recording");
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    const player = playerRef.current;
    try { player.seekTo(0, true); player.playVideo(); } catch (e) { /* noop */ }
    abortRef.current = new AbortController();
    await playWithComments({ player, comments, urls, onSpeak: setSpeakingId, signal: abortRef.current.signal });
    finishRecording();
  };

  const finishRecording = async () => {
    if (!recorderRef.current) return;
    const rec = recorderRef.current;
    recorderRef.current = null;
    abortRef.current?.abort();
    clearInterval(timerRef.current);
    pausePlayer();
    setSpeakingId(null);
    const { blob } = await rec.stop();
    if (cancelledRef.current) { setStatus(null); toast("Recording cancelled — nothing was published"); return; }
    lastBlobRef.current = blob;
    await uploadBlob(blob);
  };
  const cancelRecording = () => { cancelledRef.current = true; finishRecording(); };

  const busy = !!status || countdown !== null;
  const { playing } = useEditorPlayback({ playerRef, comments, voice, enabled: !!project && !busy && !commentOpen && !publishOpen && !voiceOpen, setSpeakingId });
  const barsMode = speakingId || preview?.state === "playing" ? "bars" : !busy && !playing ? "outline" : "hidden";

  if (!project) return <div className="min-h-screen flex items-center justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  const detailProps = { pubTitle, setPubTitle, pubDesc, setPubDesc, defaultTags, setDefaultTags, defaultTagInput, setDefaultTagInput };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="shrink-0 px-5">
        <div className="w-full max-w-5xl mx-auto h-14 border-b border-zinc-200 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="studio-back"><ArrowLeft size={18} /></Link>
          <div className="text-sm font-medium truncate flex-1 text-zinc-900">{project.source_title || project.youtube_url}</div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-5xl mx-auto p-5">
        <div className="w-full">
          <VoiceStage stageRef={stageRef} playerHostRef={playerHostRef} barsPos={barsPos} onBarsMoved={moveBars} mode={barsMode}
            locked={busy} countdown={countdown} />
          <CommentTimeline playerRef={playerRef} comments={comments} onSeek={seekTo} onRetime={retimeComment} onEdit={openEditDialog} speakingId={speakingId} disabled={busy} />
          <UnderVideoBar onPublish={() => setPublishOpen(true)} onEnterComment={openCommentDialog} onChooseVoice={() => { pausePlayer(); stopPreview(); setVoiceOpen(true); }} disabled={busy} status={status} elapsed={elapsed}
            speaking={!!speakingId} onCancel={cancelRecording} />
          <CommentsPanel comments={comments} onChange={changeComment} onDelete={removeComment} onSeek={seekTo}
            improvingIds={improvingIds} speakingId={speakingId} disabled={busy}
            preview={preview} onPreview={(c) => { pausePlayer(); togglePreview(c); }} onRepolish={(id) => { stopPreview(); repolishComment(id); }} />
          <PublishDetails {...detailProps} />
        </div>

      </div>

      <CommentDialog open={commentOpen} onOpenChange={setCommentOpen} time={commentTime} sourceTitle={project.source_title}
        initialText={editing?.text || ""} editing={!!editing}
        onSave={(text) => (editing ? changeComment(editing.id, text) : addComment(commentTime, text))} />
      <PublishVideoDialog open={publishOpen} onOpenChange={setPublishOpen} privacy={privacy} setPrivacy={setPrivacy}
        onPublish={publishVideo} commentCount={comments.length} {...detailProps} />
      <VoiceDialog open={voiceOpen} onOpenChange={setVoiceOpen} voice={voice} onSave={pickVoice} />
      <PublishResultDialog publishResult={publishResult} setPublishResult={setPublishResult}
        onRetry={() => lastBlobRef.current && uploadBlob(lastBlobRef.current)} navigate={navigate} />
    </div>
  );
}

function baseDescription(p) {
  const srcUrl = p.youtube_url || (p.video_id ? `https://youtu.be/${p.video_id}` : "");
  const recDate = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const lines = [];
  if (p.source_title) lines.push(`My reaction to "${p.source_title}".`);
  if (srcUrl) lines.push(`Original video: ${srcUrl}`);
  lines.push(`Recorded on ${recDate}.`);
  return lines.join("\n");
}
